import { Flex, CircularProgress, Text, FlexProps } from '@chakra-ui/react';
import { UseIncrementalQueryResult } from '~/queries';
import { ReactNode, useEffect, useRef } from 'react';

/**
 * InfiniteScrollForIncrementalQuery
 * @description - Used in conjunction with useIncrementalQuery or useInfiniteQuery
 *    to create a contaniner, which will display a loading indicator while
 *    the query loads, triggers the fetching of additional data when the user scrolls to the
 *    bottom of the page, and displays an indicator while additional data loads
 */

type InfiniteScrollForIncrementalQueryProps<Model> = FlexProps & {
   loadingLabel?: string;
   query: UseIncrementalQueryResult<Model>;
   contained?: boolean;
   height?: number;
   maxHeight?: number;
   children: ReactNode;
   supressNextPageSpinner?: boolean;
};

export function InfiniteScrollForIncrementalQuery<Model>({
   loadingLabel,
   query,
   contained = false,
   height,
   maxHeight,
   children,
   supressNextPageSpinner = false,
   ...flexProps
}: InfiniteScrollForIncrementalQueryProps<Model>) {
   if (contained && typeof height === 'undefined' && typeof maxHeight === 'undefined') {
      throw Error(
         '"height" or "maxHeight" are required props of "InfiniteScrollForIncrementalQuery" when "contained" is true'
      );
   }

   const { isLoading, hasNextPage, fetchNextPage, pageNumber } = query;

   const containerRef = useRef(null);

   useEffect(() => {
      const targetElement = contained ? containerRef.current : window;

      if (targetElement === null) {
         return;
      }

      function handleScrollEnd() {
         if (targetElement !== null) {
            targetElement.removeEventListener('scroll', handleScroll);
         }
         if (hasNextPage) {
            fetchNextPage();
         }
      }

      function handleScroll() {
         if (contained) {
            // If the scroll area is contained, then we know that the target element is a div.
            const container = targetElement as unknown as HTMLDivElement;

            // If the scroll area is contained, then height is a required prop.
            const containerOuterHeight = (height ?? maxHeight) as number;

            // If the container has not actually rendered, it is impossible for the
            //    user to have scrolled to the bottom
            if (container.scrollHeight === 0) {
               return;
            }

            const bottomOfContainer = container.scrollHeight - containerOuterHeight;

            if (container.scrollTop > bottomOfContainer - 10) {
               handleScrollEnd();
            }
         } else {
            const bottomOfScreen = document.body.scrollHeight - window.innerHeight;
            if (window.scrollY > bottomOfScreen - 100) {
               handleScrollEnd();
            }
         }
      }

      targetElement.addEventListener('scroll', handleScroll);

      // Call this once, in case the screen is too big for the results we've already fetched
      handleScroll();

      return () => targetElement.removeEventListener('scroll', handleScroll);

      // Include 'hasNextPage' in dependency array, so that the event-listener can be
      //    re-added if it was removed on a previous run of the query due to a
      //    smaller result set
   }, [pageNumber, hasNextPage, fetchNextPage, contained, height ?? maxHeight]);

   const contents = (
      <>
         {isLoading && (
            <Flex flex="1" height="100%" align="center" justify="center" direction="column">
               <CircularProgress isIndeterminate />
               {loadingLabel && (
                  <Text mt="2" variant="muted">
                     {loadingLabel}
                  </Text>
               )}
            </Flex>
         )}
         {!isLoading && children}
         {!supressNextPageSpinner && shouldRenderNextPageSpinner(query) && <NextPageSpinner />}
      </>
   );

   if (contained === false) {
      return contents;
   } else {
      return (
         <Flex
            ref={containerRef}
            {...flexProps}
            direction="column"
            overflowY="scroll"
            height={height}
            maxHeight={maxHeight}
         >
            {contents}
         </Flex>
      );
   }
}

export function shouldRenderNextPageSpinner<Model>(query: UseIncrementalQueryResult<Model>) {
   return query.hasNextPage && query.isFetching;
}

export function NextPageSpinner() {
   return (
      <Flex height="100px" align="center" justify="center">
         <CircularProgress isIndeterminate />
      </Flex>
   );
}
